import { Copy, ExternalLink } from 'lucide-react-native';

import {
  getOpenReferenceLabel,
  getReferenceProvenance,
  getReferenceTitle,
  type MessageReference,
} from '@/components/chat/bubbles/references/messageReferences';
import { StatementSheetDots } from '@/components/chat/statements/StatementSheetDots';
import { PublicationContent } from '@/components/publication/PublicationContent';
import { GlassIconButton } from '@/components/ui/glass';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import useClipboard from '@/hooks/useClipboard';

interface ReferenceSheetBodyProps {
  references: readonly MessageReference[];
  index: number;
  /** Whether the viewer wrote the message, so provenance reads "You" or
   * "The message". */
  viewerIsAuthor: boolean;
  /**
   * Shows the reference's number above its details. Off where the host already
   * says which reference this is, as the native sheet's pager does.
   */
  showNumber?: boolean;
}

/**
 * The content of the message reference sheet, shared by the native action
 * sheet and the web modal: the current reference laid out as a reader scans
 * it, through the shared publication summary: title, authors, source,
 * identifiers, then the summary when one is ready. Copy and open live in the host's header, see
 * `ReferenceSheetHeaderActions`. Paging is `ReferenceSheetPager`, rendered by each sheet
 * outside its scrolling area so it stays put while the content changes.
 *
 * The header names a reference by its number, not its place in the list: a
 * work whose details have not loaded is not listed yet, so the two can part
 * ways, and the header must match the [N] the reader tapped.
 *
 * A reference with no publication shows only its link, since there is
 * nothing else to show; it still pages and opens like the rest.
 *
 * The citation is not drawn: the details above already show every part of it,
 * and Copy puts the exact string on the clipboard.
 */
export function ReferenceSheetBody({
  references,
  index,
  viewerIsAuthor,
  showNumber = true,
}: ReferenceSheetBodyProps) {
  const reference = references[index];

  if (!reference) {
    return null;
  }

  return (
    <VStack space="lg" className="w-full px-5 pb-6">
      {showNumber ? (
        <Text size="sm" className="font-medium text-typography-700">
          {`Reference ${reference.number}`}
        </Text>
      ) : null}
      <PublicationContent
        title={getReferenceTitle(reference)}
        provenance={getReferenceProvenance(reference, viewerIsAuthor)}
        publication={reference.publication}
      />
    </VStack>
  );
}

/**
 * Prev and next with a dot per reference, as in the statement sheet. It sits
 * at the sheet's foot rather than in its header, because arrows beside the
 * close button put two targets a slip apart.
 */
export function ReferenceSheetPager({
  references,
  index,
  onIndexChange,
}: {
  references: readonly MessageReference[];
  index: number;
  onIndexChange: (index: number) => void;
}) {
  return (
    <StatementSheetDots
      itemNoun="reference"
      itemNumbers={references.map((reference) => reference.number)}
      total={references.length}
      currentIndex={index}
      onSelect={onIndexChange}
      onPrev={() => onIndexChange(index - 1)}
      onNext={() => onIndexChange(index + 1)}
    />
  );
}

/**
 * Copy and open for the reference on show, as glass buttons for a sheet's
 * header. Open is handed to the bubble, which owns the sheet's state outside
 * its portal, so the chat's analytics context is kept. A reference with no
 * publication has no citation, so only open shows.
 */
export function ReferenceSheetHeaderActions({
  reference,
  onOpenSource,
}: {
  reference: MessageReference | undefined;
  onOpenSource: (reference: MessageReference) => void;
}) {
  const { copyToClipboard } = useClipboard();

  if (!reference) {
    return <HStack />;
  }

  const { publication } = reference;

  return (
    <HStack space="sm" className="items-center">
      {publication ? (
        <GlassIconButton
          icon={Copy}
          accessibilityLabel="Copy AMA citation"
          onPress={() =>
            copyToClipboard(publication.citation, undefined, 'Copied AMA citation to clipboard')
          }
          fallbackClassName="bg-background-0 web:hover:bg-background-100"
        />
      ) : null}
      <GlassIconButton
        icon={ExternalLink}
        accessibilityLabel={getOpenReferenceLabel(reference)}
        onPress={() => onOpenSource(reference)}
        fallbackClassName="bg-background-0 web:hover:bg-background-100"
      />
    </HStack>
  );
}
